import { analyzerDistribution } from '@/apollo/queries/reports/analyzerDistribution.gql'
import { categoryDistribution } from '@/apollo/queries/reports/categoryDistribution.gql'
import { defineComponent } from 'vue'
import { GraphqlQueryResponse } from '~/types/apolloTypes'
import { Dataset, IssueDistributionT, ReportPageT } from '~/types/reportTypes'
import { IssueDistribution, ReportLevel } from '~/types/types'
import {
  getFormattedDistributionChartData,
  getMaxDigitDistributionHistoricalValues
} from '~/utils/reports'
import ReportMixin from './reportMixin'

export default defineComponent({
  name: 'DistributionReportMixin',

  mixins: [ReportMixin],

  data() {
    return {
      activeFilter: IssueDistributionT.CATEGORY,
      distributionStats: [] as Array<IssueDistribution>,
      analyzerDataset: [] as Dataset[],
      categoryDataset: [] as Dataset[],
      datasetNames: [],
      categoryOthersDatasetNames: [] as string[],
      analyzerOthersDatasetNames: [] as string[]
    }
  },

  computed: {
    shouldChartBeShown(): boolean {
      return !(
        this.historicalValuesLoading ||
        this.labels.length < 2 ||
        this.issueDistributionData.length === 0
      )
    },

    issueDistributionData(): Array<Dataset> {
      return this.activeFilter === IssueDistributionT.CATEGORY
        ? this.categoryDataset
        : this.analyzerDataset
    },

    othersDatasetNames(): Array<string> {
      return this.activeFilter === IssueDistributionT.CATEGORY
        ? this.categoryOthersDatasetNames
        : this.analyzerOthersDatasetNames
    },

    currentVal(): number {
      return this.report?.currentValue ?? 0
    },

    maxDigitHistoricValues(): number {
      return getMaxDigitDistributionHistoricalValues(this.activeFilter, this.historicalValues)
    },

    reportRerenderKey(): string {
      return `${this.activeFilter}-${this.dateRangeFilter}-${this.maxBarClip}-${this.issueDistributionData.length}`
    }
  },

  methods: {
    getActiveFilter(): IssueDistributionT {
      if (
        Object.values(IssueDistributionT).includes(this.$route.query.filter as IssueDistributionT)
      ) {
        return this.$route.query.filter as IssueDistributionT
      }

      return IssueDistributionT.CATEGORY
    },

    async fetchDistributionStats(
      reportKey: ReportPageT,
      level: ReportLevel,
      objectId: string,
      distributionType: IssueDistributionT,
      refetch = false
    ): Promise<Array<IssueDistribution>> {
      this.distributionStatsLoading = true

      const distributionQuery =
        distributionType === IssueDistributionT.ANALYZER
          ? analyzerDistribution
          : categoryDistribution

      try {
        const response = (await this.$fetchGraphqlData(
          distributionQuery,
          {
            reportKey,
            level,
            objectId
          },
          refetch
        )) as GraphqlQueryResponse

        return (
          distributionType === IssueDistributionT.ANALYZER
            ? response.data.issueDistributionByAnalyzer
            : response.data.issueDistributionByCategory
        ) as Array<IssueDistribution>
      } catch (e) {
        this.$logErrorAndToast(
          e as Error,
          'Unable to fetch issue distribution, please contact support.'
        )
      } finally {
        this.distributionStatsLoading = false
      }
      return []
    },

    setDistributionChartData(reportKey: ReportPageT): void {
      const analyzerValues = this.historicalValues.values.analyzer as Record<string, number[]>

      if (analyzerValues) {
        const [dataset, othersDatasetNames] = getFormattedDistributionChartData(
          analyzerValues,
          reportKey
        )
        this.analyzerDataset = dataset
        this.analyzerOthersDatasetNames = othersDatasetNames
      }

      const categoryValues = this.historicalValues.values.category as Record<string, number[]>

      if (categoryValues) {
        const [dataset, othersDatasetNames] = getFormattedDistributionChartData(
          categoryValues,
          reportKey
        )
        this.categoryDataset = dataset
        this.categoryOthersDatasetNames = othersDatasetNames
      }
    }
  }
})
